import axios from 'axios'

const state = {
  collectionId: 0,
  productsPerPage: 0,
  page: 1,
  pages: 1,
  filters: {},
  filterIds: [],
  tags: {},
  tagIds: [],
  products: [],
  searchQuery: null,
}

/**
 * GETTERS
 */
const getters = {
  getTag: state => id => state.tags[id],
  getSelectedTagIds: state => state.tagIds.filter(key => state.tags[key].selected == true),
  getSelectedTags: (state, getters) => {
    let result = []
    getters.getSelectedTagIds.forEach(id => result.push(state.tags[id]))
    return result
  },
  getSelectedFilters: state => {
    let results = {}
    for (const [filterId, filter] of Object.entries(state.filters)) {
      filter.tagIds.forEach(tagId => {
        if (state.tags[tagId].selected == true) {
          results[filterId] ? results[filterId].push(tagId) : (results[filterId] = [tagId])
        }
      })
    }
    return results
  },
  getFilterById: state => id => {
    return state.filters.find(f => f.filterOptionId === id)
  },
  getSearchQuery: state => {
    if (state.searchQuery) {
      return { q: state.searchQuery }
    }
    return null
  },
}

/**
 * MUTATIONS
 */
const mutations = {
  selectTag(state, tagId) {
    if (state.tags[tagId]) {
      state.tags[tagId].selected = true
    }
  },
  setCollectionId(state, payload) {
    state.collectionId = payload
  },
  setProductsPerPage(state, payload) {
    state.productsPerPage = payload
  },
  setFilters(state, payload) {
    state.filters = payload
  },
  setFilterIds(state, payload) {
    state.filterIds = payload
  },
  setTags(state, payload) {
    state.tags = payload
  },
  setTagIds(state, payload) {
    state.tagIds = payload
  },
  setProducts(state, payload) {
    state.products = payload
  },
  setSneakPeakImages(state, payload) {
    state.products.forEach(p => (p.sneakPeak = payload[p.id]))
  },
  setPage(state, payload) {
    state.page = payload
  },
  setPages(state, payload) {
    state.pages = payload
  },
  setSearchQuery(state, payload) {
    state.searchQuery = payload
  },
  toggleTag(state, tagId) {
    state.tags[tagId].selected = !state.tags[tagId].selected
  },
}

/**
 * ACTIONS
 */
const actions = {
  async changePage({ commit, dispatch, getters, state, rootState }, page) {
    commit('setPage', page)
    let result = await requestProducts({ state, rootState, getters })
    commit('setProducts', result.data.products)
    dispatch('loadSneakPeakImages')
  },

  async loadFiltersAndProductsFromApi({ commit, dispatch, state, rootState }, { filterQuery, page }) {
    if (Number.isInteger(page)) {
      commit('setPage', page)
    }
    let result = await requestProducts({
      state,
      rootState,
      extraParams: filterQuery,
      build_filter_tree: true,
    })
    commit('setSearchQuery', filterQuery.q)
    dispatch('setFilterOptions', result.data.filter.options)
    dispatch('setProductsAndPages', result.data)
    dispatch('loadSneakPeakImages')
    // Filter out search query ("q") and flatten tags
    let tags = Object.values(filterQuery)
      .filter(f => Array.isArray(f))
      .flat()
    dispatch('selectTags', tags)
  },

  async loadFiltersFromApi({ dispatch, state, rootState }) {
    let result = await requestProducts({ state, rootState, build_filter_tree: true })
    dispatch('setFilterOptions', result.data.filter.options)
  },

  loadProductsFromJson({ commit, dispatch }, { products, totalProductsCount }) {
    products = products.filter(p => !p.tags.includes('pfs:hidden'))
    products = products.splice(0, 42)
    commit('setProducts', products)
    commit('setPages', Math.ceil(totalProductsCount / state.productsPerPage))
    commit('setPage', 1)
    dispatch('loadSneakPeakImages')
  },
  loadSneakPeakImages({ commit, state }) {
    // Use Storefront API to:
    //    1. Get the first variant for each product
    //    2. Get the first custom fields image for each of these variants
    let variantIds = state.products.map(p => `gid://shopify/ProductVariant/${p.variants[0].id}`)
    axios
      .post(
        'https://ahlem-eyewear.myshopify.com/api/2022-01/graphql.json',
        {
          query: `
          query VariantImages($variantIds: [ID!]!) {
            nodes(ids: $variantIds) {
              ...on ProductVariant {
                id,
                title,
                sneakPeakImages: metafield(namespace: "custom_fields", key: "variant_images") {
                  value
                },
                product { id, title }
              }
            }
          }
        `,
          variables: { variantIds: variantIds },
        },
        {
          headers: { 'X-Shopify-Storefront-Access-Token': '70c1a347a516396b3278c5e2a62c6302' },
        },
      )
      .then(result => {
        if (result.data.data.nodes.length) {
          let finalResult = {}
          result.data.data.nodes.forEach(o => {
            if (o) {
              let image = null
              if (o.sneakPeakImages) {
                let images = JSON.parse(o.sneakPeakImages.value)
                image = images[0].variant_images
              }
              // Extract the numeric part of the product ID
              const numericProductId = o.product.id.replace(/\D/g, '');

              // Use the numeric product ID as the key in the finalResult object
              finalResult[numericProductId] = image;
            }
          })
          commit('setSneakPeakImages', finalResult)
        }
      })
  },

  selectTags({ commit }, tagIds) {
    tagIds.forEach(tagId => commit('selectTag', tagId))
  },

  setFilterOptions({ commit }, options) {
    let activeFilters = options.filter(option => option.status === 'active')
    let filters = {}
    let filterIds = []
    let tags = {}
    activeFilters.forEach(f => {
      if (Array.isArray(f.values) && f.values.length > 0) {
        let filter = { label: f.label, tagIds: [] }
        f.values.forEach(v => {
          filter.tagIds.push(v.key)
          tags[v.key] = { id: v.key, selected: false }
        })
        filters[f.filterOptionId] = filter
        filterIds.push(f.filterOptionId)
      }
    })
    commit('setFilters', filters)
    commit('setFilterIds', filterIds)
    commit('setTags', tags)
    commit('setTagIds', Object.keys(tags))
  },

  async setProductsAndPages({ commit, state }, data) {
    commit('setProducts', data.products)
    commit('setPages', Math.ceil(data.total_product / state.productsPerPage))
  },

  async toggleTag({ commit, dispatch, state, rootState, getters }, tagId) {
    commit('setPage', 1)
    commit('toggleTag', tagId)
    let result = await requestProducts({ state, rootState, getters })
    await dispatch('setProductsAndPages', result.data)
  },
}

const requestProducts = async function ({
  state,
  getters,
  rootState,
  extraParams = {},
  build_filter_tree = false,
}) {
  let selectedFilters = getters ? getters.getSelectedFilters : null
  let searchQuery = getters ? getters.getSearchQuery : null
  let sort = state.collectionId > 0 ? 'manual' : 'title-ascending'
  let config = rootState.config
  let apiUrl = searchQuery || extraParams.q ? config.searchApiUrl : config.filterApiUrl
  let result = await axios.get(apiUrl, {
    params: {
      limit: state.productsPerPage,
      sort: sort,
      display: 'grid',
      event_type: 'filter',
      page: state.page,
      ...extraParams,
      ...selectedFilters,
      ...searchQuery,
      build_filter_tree: build_filter_tree,
      collection_scope: state.collectionId,
      t: rootState.config.themeId,
      shop: rootState.config.shopDomain,
      // product_available: true,
    },
  })
  let products = result.data.products
  products.forEach(p => {
    p.price = p.price_min * 100
    p.featured_image = p.images[1].replace('https:', '')
  })
  return result
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
